// app/src/lib/locale-sync.jsx — the account-language plumbing (D-AUTH §3), the SPA
// translation of Quocia-FB's LocaleSync + uauLocale contract:
//
//  · SIGNED-IN, THE ACCOUNT WINS: when /v1/me carries ui_locale, the UI switches to it
//    and the host-only userLang cookie is self-healed into lockstep (the reference's
//    2026-06-10 cure: the cookie never depends on a server round-trip succeeding).
//    An account with NO preference yet (pre-D-AUTH users) is healed UPWARD once with
//    the currently-resolved locale.
//  · SWITCHING writes all three in one breath: i18n state (instant re-render), the
//    userLang cookie, and PUT /v1/me/locale (the record) — useSwitchLocale().
//  · SIGN-OUT clears userLang (the reference clears it in /api/session/clear).
//  · SIGNED-OUT AUTH PAGES seed the shared `lang` cookie when missing (the reference
//    middleware's UAU seeding) — useSeedLangCookie(). The cookie Domain comes from
//    VITE_SHARED_COOKIE_DOMAIN ('.quocia.com' on the real domains; unset on localhost/
//    web.app → host-only, and on the quicketing.com admin the cookie simply can't be
//    shared — resolution degrades to browser → default by design).

import { useCallback, useEffect, useRef } from 'react';
import { pickInitialLocale } from '@common/i18n.js';
import {
  writeUserLangCookie, clearUserLangCookie, seedLangCookieIfMissing, getPublicLocaleFromCookies,
} from '@common/locale-cookie.js';
import { useI18n, LOCALE_TAGS } from '../i18n/index.jsx';
import { useSession } from './session.jsx';

export const SHARED_COOKIE_DOMAIN = import.meta.env.VITE_SHARED_COOKIE_DOMAIN || null;

// the uauLocale every auth call sends — the PUBLIC device preference, deliberately not
// userLang (the reference's rule: seed the account from the public choice at signup).
export function publicLocale() {
  return getPublicLocaleFromCookies(LOCALE_TAGS) || 'en-US';
}

export function useSwitchLocale() {
  const { setLocale } = useI18n();
  const { authed, api, refreshMe } = useSession();
  return useCallback(async (tag) => {
    setLocale(tag); // instant — the i18n context re-renders every consumer NOW
    if (authed) {
      writeUserLangCookie(tag); // cookie half of the lockstep
      const r = await api.put('me/locale', { ui_locale: tag }); // record half
      if (r.kind === 'ok') refreshMe(); // me.ui_locale follows, so LocaleSync stays quiet
    }
  }, [setLocale, authed, api, refreshMe]);
}

// mount once on auth pages: a UAU leaves with a `lang` cookie the whole estate shares
export function useSeedLangCookie() {
  const { registry } = useI18n();
  useEffect(() => {
    seedLangCookieIfMissing(LOCALE_TAGS, {
      domain: SHARED_COOKIE_DOMAIN,
      resolve: () => pickInitialLocale(registry, null), // browser → en-US
    });
  }, [registry]);
}

export default function LocaleSync() {
  const { tag, setLocale } = useI18n();
  const { authed, me, api } = useSession();
  const wasAuthed = useRef(false);
  const healed = useRef(null); // one upward heal per account per mount

  useEffect(() => {
    if (!me || !me.user_id) return;
    if (me.ui_locale && LOCALE_TAGS.includes(me.ui_locale)) {
      if (me.ui_locale !== tag) setLocale(me.ui_locale); // the account preference wins
      writeUserLangCookie(me.ui_locale); // self-heal the cookie into lockstep
    } else if (healed.current !== me.user_id) {
      healed.current = me.user_id;
      writeUserLangCookie(tag);
      api.put('me/locale', { ui_locale: tag }); // heal the record upward once
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me && me.user_id, me && me.ui_locale]);

  useEffect(() => {
    if (authed) { wasAuthed.current = true; return; }
    if (wasAuthed.current) { clearUserLangCookie(); wasAuthed.current = false; }
  }, [authed]);

  return null;
}
